import { ref, computed } from 'vue'

import * as Sentry from '@sentry/vue'
import { defineStore } from 'pinia'

import { createOidcApiInstance, createSimpleOidcApiInstance } from '@/utils/api/axiosInstance'
import { getProtectedImage } from '@/utils/api/fetchAuthImage'
import { handleApiStatus } from '@/utils/api/handleAPI'
import { authEncryptPassword } from '@/utils/auth/authEncrypt'
import { initializeUserManager, createAndStoreUser } from '@/utils/auth/oidcClient'

import * as aggr from '@/api-generator/aggr'
import * as oidc from '@/api-generator/oidc/index'
import { RegisterType, OauthType, AvatarSize } from '@/constant/user'

interface UserInfo {
  email: string
  name: string
  picture: string
  is_custom_avatar: boolean
  provider: string
  role: aggr.SystemUserRole
  is_company_owner: boolean
  is_native_account: boolean
  invitation_code?: string
  is_system_staff?: boolean
}

export const useAuthStore = defineStore('auth', () => {
  const $reset = () => {}

  interface LoginData {
    grant_type: string
    client_id: string
    client_secret: string
    username: string
    password: string
    scope: string
  }

  const userInfo = ref<UserInfo>()

  const registerType = ref<RegisterType>()
  const oauthType = ref<OauthType>()

  const setRegisterType = (type: RegisterType) => {
    registerType.value = type
  }

  const setOauthType = (type: OauthType) => {
    oauthType.value = type
  }

  const CLIENT_ID = import.meta.env.VITE_CLIENT_ID
  const CLIENT_SECRET = import.meta.env.VITE_CLIENT_SECRET
  const SCOPE = import.meta.env.VITE_SCOPE

  const passwordLogin = async (username: string, password: string) => {
    try {
      const authEncryptedPassword = authEncryptPassword(password)

      if (!authEncryptedPassword) {
        throw new Error('password encrypt error')
      }

      const data = ref<LoginData>({
        username,
        password: authEncryptedPassword,
        grant_type: 'password',
        client_id: CLIENT_ID,
        client_secret: CLIENT_SECRET,
        scope: SCOPE
      })

      const AuthorizationApi = createSimpleOidcApiInstance(oidc.AuthorizationApi)

      const res: any = await AuthorizationApi.postAuthorizationExchange(
        data.value.grant_type,
        data.value.client_id,
        data.value.client_secret,
        data.value.username,
        data.value.password,
        data.value.scope
      )
      const { access_token, token_type, expires_in, refresh_token } = res.data
      await createAndStoreUser(access_token, refresh_token, token_type, expires_in)
      await initializeUserManager()
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const getUserInfo = async () => {
    const UserinfoApi = createOidcApiInstance(oidc.UserinfoApi)

    const res: any = await handleApiStatus(() => UserinfoApi.getUserinfoUserinfo())
    updateUserInfo(
      res.data.email,
      res.data.name,
      res.data.picture,
      res.data.is_custom_avatar,
      res.data.provider,
      res.data.role,
      res.data.is_company_owner,
      res.data.is_native_account,
      res.data.invitation_code,
      res.data.is_system_staff
    )
  }

  const updateUserInfo = (
    email: string,
    name: string,
    picture: string,
    is_custom_avatar: boolean,
    provider: string,
    role: aggr.SystemUserRole,
    is_company_owner: boolean,
    is_native_account: boolean,
    invitation_code: string | undefined = undefined,
    is_system_staff: boolean | undefined = undefined
  ) => {
    userInfo.value = {
      email,
      name,
      picture,
      is_custom_avatar,
      provider,
      role,
      is_company_owner,
      is_native_account,
      invitation_code,
      is_system_staff
    }

    // 標記登入者身分，讓 GlitchTip 的每筆錯誤都能對應到人
    Sentry.setUser({ email, username: name })
  }

  const isNativeAccount = computed(() => {
    return userInfo.value?.is_native_account ?? false
  })

  const hasUserInfo = () => {
    if (!userInfo.value) {
      return false
    }
    return true
  }

  const handlePermission = (roles: aggr.SystemUserRole[]) => {
    if (!userInfo.value || !userInfo.value.role) {
      return false
    }
    if (!roles.includes(userInfo.value?.role)) {
      return false
    }
    return true
  }

  // 使用者頭像
  const userSMAvatarUrl = ref('')
  const userHDAvatarUrl = ref('')

  const setUserBlobAvatar = async (picSize: AvatarSize) => {
    if (!userInfo.value?.picture) {
      return
    }

    try {
      const blobImg = await getProtectedImage(`${userInfo.value.picture}?size=${picSize}`)
      if (picSize === AvatarSize.SMALL) {
        userSMAvatarUrl.value = blobImg
      } else if (picSize === AvatarSize.LARGE) {
        userHDAvatarUrl.value = blobImg
      }
    } catch (error) {
      console.error('Failed to load user avatar:', error)
    }
  }

  const allowedAdminRoles: aggr.SystemUserRole[] = [aggr.SystemUserRole.Admin, aggr.SystemUserRole.SuperUser]

  // 確認是否有後台瀏覽權限
  const isAdmin = computed(() => {
    if (!userInfo.value) {
      return false
    }

    return (
      allowedAdminRoles.includes(userInfo.value.role) ||
      (userInfo.value.role === aggr.SystemUserRole.Guest && userInfo.value.is_company_owner)
    )
  })

  return {
    $reset,
    userInfo,
    passwordLogin,
    getUserInfo,
    hasUserInfo,
    handlePermission,
    registerType,
    oauthType,
    setRegisterType,
    setOauthType,
    userSMAvatarUrl,
    userHDAvatarUrl,
    setUserBlobAvatar,
    isAdmin,
    isNativeAccount
  }
})
